import httpService from "../../utils/httpService"
import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {UserDealer} from "../App/models"
import {getAccessToken, getSelectedRegionId} from "../../utils/common"
import {setToastr} from "../App/appSlice"

interface Schedule {
    id: string
    start: string
    end: string
    isAllDay: boolean
    title: string
    description: string
    recurrenceRule: string | null
    recurringGroup: string | null
    recurrenceExceptions: string | null
}

export interface CreateScheduleModel {
    title: string
    description: string
    startDateTime: string
    endDateTime: string
    breakStartDateTime: string
    breakEndDateTime: string
    weekDays: string
    lastDate: string
    UpdatedByAppUserID: string
}

export interface SchedulesAndBreak {
    dvsaAvailabilityID: number
    dealerID: number
    eventTitle: string
    startDateTime: string
    endDateTime: string
    recurringGroup: any
    updatedByAppUserID: string
    updatedDate: string
    conflicts: number
    recType: string
    minTestStartDate: string
    maxTestEndDate: string
}
export interface UpdateScheduleModel {
    dvsaAvailabilityID: string
    startDateTime: string
    endDateTime: string
    recurringGroup: string | null
    updatedByAppUserId: string
    eventType: string
}

export interface DeleteScheduleModel {
    dvsaAvailabilityID: string
    recurringGroup: string | null
    startDate: string
    endDate: string
    eventType: string
}

export interface ReccurringEventsParam {
    dealerId: string
    recurringGroup: string
    fromDate: string
    schStart: string
    schEnd: string
    eventType: string
}

interface SelectedSlot {
    start: Date
    end: Date
    breakStart: Date | null
    breakEnd: Date | null
    id: string | null
    recurrendeId: string | null
    recType: string
    conflicts: number
    minTestStartDate: Date | null
    maxTestEndDate: Date | null
}

type schedulesState = {
    loading: boolean
    schedules: SchedulesAndBreak[]
    // error: string | null
    // successMessage: string | null
    selectedSlot: SelectedSlot | null
    showNotification: boolean
    selectedDealer: UserDealer | null
    showAddEditPopUp: boolean
    showDialogLoader: boolean
    recurrenceLoading: boolean
    recurrenceSchedules: Schedule[] | null
}

const initialState: schedulesState = {
    loading: false,
    schedules: [],
    // error: null,
    // successMessage: null,
    selectedSlot: null,
    showNotification: false,
    selectedDealer: null,
    showDialogLoader: false,
    showAddEditPopUp: false,
    recurrenceLoading: false,
    recurrenceSchedules: null,
}

const schedulesSlice = createSlice({
    name: "schedules",
    initialState,
    reducers: {
        showLoader(state, action: PayloadAction<boolean>) {
            state.loading = action.payload
        },
        setSchedules(state, action: PayloadAction<SchedulesAndBreak[]>) {
            state.schedules = action.payload
        },
        setSelectedSlot(state, action: PayloadAction<SelectedSlot | null>) {
            state.selectedSlot = action.payload
        },
        // setError(state, action: PayloadAction<string | null>) {
        //     state.error = action.payload
        // },
        // setSuccessMessage(state, action: PayloadAction<string | null>) {
        //     state.successMessage = action.payload
        // },
        setShowNotification(state, action: PayloadAction<boolean>) {
            state.showNotification = action.payload
        },
        setSelectedDealer(state, action: PayloadAction<UserDealer>) {
            state.selectedDealer = action.payload
        },
        setShowAddEditPopUp(state, action: PayloadAction<boolean>) {
            state.showAddEditPopUp = action.payload
        },
        setDialogLoading(state, action: PayloadAction<boolean>) {
            state.showDialogLoader = action.payload
        },
        setRecurrenceSchedulesLoading(state, action: PayloadAction<boolean>) {
            state.recurrenceLoading = action.payload
        },
        setRecurrenceSchedules(state, action: PayloadAction<Schedule[] | null>) {
            state.recurrenceSchedules = action.payload
        },
    },
})

export const {
    setSchedules,
    showLoader,
    setSelectedSlot,
    // setError,
    // setSuccessMessage,
    setShowNotification,
    setSelectedDealer,
    setShowAddEditPopUp,
    setDialogLoading,
    setRecurrenceSchedulesLoading,
    setRecurrenceSchedules,
} = schedulesSlice.actions

export default schedulesSlice.reducer

export const fetchSchedules =
    (dealerId: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(showLoader(true))
        dispatch(setSchedules([]))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/dealers/${dealerId}/schedules?withBreak=true`)
            .then(res => {
                dispatch(setSchedules(res.data))
                dispatch(showLoader(false))
            })
            .catch(error => {
                dispatch(showLoader(false))
            })
    }

export const fetchRecurrenceSchedules =
    (param: ReccurringEventsParam): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setRecurrenceSchedulesLoading(true))
        httpService(getAccessToken(getState()))
            .get(
                `regions/${getSelectedRegionId(getState())}/dealers/${param.dealerId}/schedules/${
                    param.recurringGroup
                }/${param.eventType}?fromDate=${param.fromDate}&&schStart=${param.schStart}&&schEnd=${param.schEnd}`,
            )
            .then(res => {
                dispatch(setRecurrenceSchedules(res.data))
                dispatch(setRecurrenceSchedulesLoading(false))
            })
            .catch(error => {
                dispatch(setRecurrenceSchedulesLoading(false))
            })
    }

export const createSchedule =
    (dealerId: string, data: CreateScheduleModel): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setDialogLoading(true))
        httpService(getAccessToken(getState()))
            .post(`regions/${getSelectedRegionId(getState())}/dealers/${dealerId}/schedules`, data)
            .then(res => {
                if (res.data < 1) {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Unable to create availability",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                } else {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Availability created successfully",
                            delay: 3000,
                            title: "Success",
                            type: "light",
                            description: "",
                        }),
                    )
                }
                dispatch(setDialogLoading(false))
                dispatch(setSelectedSlot(null))
                dispatch(setShowNotification(true))
                dispatch(setShowAddEditPopUp(false))

                dispatch(fetchSchedules(dealerId))
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
                dispatch(setDialogLoading(false))
                dispatch(setSelectedSlot(null))
                dispatch(setShowNotification(true))
                dispatch(setShowAddEditPopUp(false))
            })
    }

export const updateSchedule =
    (dealerId: string, data: UpdateScheduleModel): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setDialogLoading(true))
        httpService(getAccessToken(getState()))
            .put(`regions/${getSelectedRegionId(getState())}/dealers/${dealerId}/schedules/${data.eventType}`, data)
            .then(res => {
                if (res.data < 0) {
                    setToastr({
                        isActive: true,
                        message: "Unable to update availability",
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    })
                } else if (res.data === 0) {
                    setToastr({
                        isActive: true,
                        message: "No schedules updated, there may be booking on the selected days",
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    })
                } else {
                    setToastr({
                        isActive: true,
                        message: `${res.data} Schedules updated successfully`,
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    })
                }
                dispatch(fetchSchedules(dealerId))
                dispatch(setDialogLoading(false))
                dispatch(setSelectedSlot(null))
                dispatch(setShowNotification(true))
                dispatch(setShowAddEditPopUp(false))
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
                dispatch(setDialogLoading(false))
                dispatch(setSelectedSlot(null))
                dispatch(setShowNotification(true))
                dispatch(setShowAddEditPopUp(false))
            })
    }

export const deleteSchedule =
    (dealerId: string, data: DeleteScheduleModel): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setDialogLoading(true))
        httpService(getAccessToken(getState()))
            .post(
                `regions/${getSelectedRegionId(getState())}/dealers/${dealerId}/deleteschedules/${data.eventType}`,
                data,
            )
            .then(res => {
                if (res.data < 0) {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Unable to delete availability",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                } else if (res.data === 0) {
                    dispatch(
                        dispatch(
                            setToastr({
                                isActive: true,
                                message: "No schedules deleted, there may be booking on the selected days",
                                delay: 3000,
                                title: "Error",
                                type: "danger",
                                description: "",
                            }),
                        ),
                    )
                } else {
                    dispatch(
                        dispatch(
                            setToastr({
                                isActive: true,
                                message: `${res.data} Schedules deleted successfully`,
                                delay: 3000,
                                title: "Success",
                                type: "light",
                                description: "",
                            }),
                        ),
                    )
                }
                dispatch(fetchSchedules(dealerId))
                dispatch(setShowNotification(true))
                dispatch(setDialogLoading(false))
                dispatch(setSelectedSlot(null))
                dispatch(setShowAddEditPopUp(false))
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
                dispatch(setShowNotification(true))
                dispatch(setDialogLoading(false))
                dispatch(setSelectedSlot(null))
                dispatch(setShowAddEditPopUp(false))
            })
    }
